import { omit } from 'lodash';
import { nanoid } from 'nanoid';
import { v4 } from 'uuid';

import { IndexName } from '@enums/IndexName';
import FilterDefinition, {
  LogicalOperator,
  LogicalOperators,
  SearchFilterOperator,
  SearchFilterOperators,
} from '@models/FilterDefinition';
import { OrderBy, SortOrder, SortOrders } from '@models/OrderBy';
import { TOrderProvidersBy } from '@models/OrderProvidersBy';
import {
  FilterField,
  FilterGroup,
  MinifiedFilterField,
  MinifiedFilterFieldKeyMap,
  MinifiedFilterGroup,
  MinifiedFilterGroupKeyMap,
} from '@models/SearchFilter';
import { SearchPageQueryParams, SearchQuery } from '@models/SearchQuery';
import { getNewFilterGroup } from '@utils/filterUtils';
import { decodeBase64, encodeBase64 } from '@utils/stringUtils';

/**
 * Serializes an object into a URL-safe string.
 */
export const serialize = <T>(data: T) => {
  try {
    const json = JSON.stringify(data);
    return encodeBase64(json);
  } catch (e) {
    console.error(e);
  }

  return '';
};

/**
 * Deserializes a URL-safe string into its original object.
 */
export const deserialize = <T>(str?: string) => {
  if (!str) return;

  try {
    const json = decodeBase64(decodeURIComponent(str));
    return JSON.parse(json) as T;
  } catch (e) {
    console.error(e);
  }
};

export const getQueryText = (query: SearchPageQueryParams) => query.q || '';
export const getPageNo = (query: SearchPageQueryParams) => Number(query.p) || 1;
export const getDistinctBy = (query: SearchPageQueryParams) => query.distinctBy || '';

export const getFilterCount = (filterGroup: FilterGroup): number =>
  filterGroup.fields && filterGroup.fields.length > 0
    ? filterGroup.fields.reduce((acc: number, cur) => {
        let curFilterCount = 0;
        if (cur.value != null && cur.value !== '') curFilterCount = 1;
        else if (cur.filter?.fields && cur.filter.fields.length > 0)
          curFilterCount = getFilterCount(cur.filter);
        return acc + curFilterCount;
      }, 0)
    : 0;

export const stripFilterGroup = (filterGroup?: FilterGroup): FilterGroup | undefined => {
  if (!filterGroup) return undefined;

  const curFields = filterGroup.fields?.reduce((acc: FilterField[], cur) => {
    if (!cur.propertyName && !cur.filter && (cur.value == null || cur.value === '')) return acc;

    const stripped = stripFilterGroup(cur.filter);

    const tmp: FilterField = stripped ? { ...cur, filter: stripped } : omit(cur, 'filter');

    if ((!tmp.filter || tmp.filter.fields.length === 0) && (tmp.value == null || tmp.value === ''))
      return acc;

    return [...acc, tmp];
  }, []);

  if (!curFields || curFields.length === 0) return undefined;

  if (curFields.length === 1 && curFields[0]?.filter?.isRootLevelFilter) return curFields[0].filter;

  return { ...filterGroup, fields: curFields };
};

/**
 *  Minified keys of the filter object in order to make it as small as possible (URL friendly)
 */
export const compressFilterKeys = (filterGroup: FilterGroup): MinifiedFilterGroup =>
  Object.entries(MinifiedFilterGroupKeyMap).reduce(
    (acc: MinifiedFilterGroup, [longKey, shortKey]) => {
      if ((longKey as keyof FilterGroup) === 'fields')
        return {
          ...acc,
          [shortKey as keyof MinifiedFilterGroup]: filterGroup.fields.map(field =>
            Object.entries(MinifiedFilterFieldKeyMap).reduce(
              (fieldsAcc, [fieldLongKey, fieldShortKey]) => {
                const value = field[fieldLongKey as keyof FilterField];

                if (value == null) return fieldsAcc;

                return {
                  ...fieldsAcc,
                  [fieldShortKey as keyof MinifiedFilterField]:
                    (fieldLongKey as keyof FilterField) === 'filter'
                      ? compressFilterKeys(value as FilterGroup)
                      : field[fieldLongKey as keyof FilterField],
                };
              },
              {}
            )
          ),
        };

      if ((longKey as keyof FilterGroup) === 'isRootLevelFilter')
        return {
          ...acc,
          [shortKey as keyof MinifiedFilterGroup]: Number(
            filterGroup[longKey as keyof FilterGroup]
          ),
        };

      return {
        ...acc,
        [shortKey as keyof MinifiedFilterGroup]: filterGroup[longKey as keyof FilterGroup],
      };
    },
    {} as MinifiedFilterGroup
  );

/**
 *  Restores minified (URL friendly) keys of the filter object
 */
export const decompressFilterKeys = (filterGroup: MinifiedFilterGroup): FilterGroup =>
  Object.entries(MinifiedFilterGroupKeyMap).reduce((acc: FilterGroup, [longKey, shortKey]) => {
    if ((longKey as keyof FilterGroup) === 'fields')
      return {
        ...acc,
        [longKey as keyof FilterGroup]: filterGroup.f.map(field =>
          Object.entries(MinifiedFilterFieldKeyMap).reduce(
            (fieldsAcc, [fieldLongKey, fieldShortKey]) => {
              const value = field[fieldShortKey as keyof MinifiedFilterField];
              if (value == null) return fieldsAcc;

              return {
                ...fieldsAcc,
                [fieldLongKey as keyof FilterField]:
                  (fieldLongKey as keyof FilterField) === 'filter'
                    ? decompressFilterKeys(value as MinifiedFilterGroup)
                    : field[fieldShortKey as keyof MinifiedFilterField],
              };
            },
            { id: nanoid(6) }
          )
        ),
      };

    if ((longKey as keyof FilterGroup) === 'isRootLevelFilter')
      return {
        ...acc,
        [longKey as keyof FilterGroup]: Boolean(filterGroup[shortKey as keyof MinifiedFilterGroup]),
      };

    return {
      ...acc,
      [longKey as keyof FilterGroup]: filterGroup[shortKey as keyof MinifiedFilterGroup],
    };
  }, {} as FilterGroup);

export const getFilter = (
  searchParams: URLSearchParams,
  fallback: FilterGroup = getNewFilterGroup(true)
) => {
  const deserialized = deserialize<MinifiedFilterGroup>(searchParams.get('filter')?.toString());

  if (deserialized) {
    try {
      return stripFilterGroup(decompressFilterKeys(deserialized)) ?? fallback;
    } catch (e) {
      console.error(e);
    }
  }

  return fallback;
};

export const getOrderBy = (searchParams: URLSearchParams, fallback: OrderBy[] = []) =>
  deserialize<OrderBy[]>(searchParams.get('orderBy')?.toString()) ?? fallback;

export const getOrderProvidersBy = (
  searchParams: URLSearchParams & { orderProvidersBy?: string }
) => deserialize<TOrderProvidersBy | undefined>(searchParams.get('orderProvidersBy')?.toString());

export const getSortableFieldDefs = (filterDefs: FilterDefinition[] | undefined = []) =>
  filterDefs
    .reduce((acc: FilterDefinition[], cur) => {
      let ret: FilterDefinition[] = [...acc];

      if (cur.isSortable) ret = [...ret, cur];

      if (cur.fields && cur.fields.length > 0)
        ret = [
          ...ret,
          ...cur.fields
            .filter(fd => fd.isSortable)
            .map(fd => ({
              ...fd,
              propertyName: `${cur.propertyName}/${fd.propertyName}`,
              displayName: `${fd.displayName} ${cur.displayName.toLowerCase()}`,
            })),
        ];

      return ret;
    }, [])
    .sort((a, b) => (a.displayName > b.displayName ? 1 : -1));

/**
 * API limit
 */
export const maxSkip = 100000;

export const getPageCount = (totalCount: number, pageSize: number) =>
  Math.min(Math.ceil(totalCount / pageSize), 100000 / pageSize);

export const buildOrderByStr = (orderBy: OrderBy[]) =>
  orderBy.length > 0
    ? orderBy.map(({ field, sortOrder }) => `${field} ${sortOrder}`).join(',')
    : '';

export const parseOrderByStr = (orderByStr?: string): OrderBy[] | undefined =>
  orderByStr
    ? orderByStr.split(',').map(str => {
        const [field, sortOrder] = str.split(' ');
        return { field, sortOrder: sortOrder as SortOrder, id: v4() };
      })
    : [];

export const isFilterField = (obj: unknown): obj is FilterField => {
  if (!obj || typeof obj !== 'object') return false;

  if ('value' in obj && typeof obj.value !== 'boolean' && typeof obj.value !== 'string')
    return false;

  if ('operator' in obj && !SearchFilterOperators.includes(obj.operator as SearchFilterOperator))
    return false;

  if ('id' in obj && typeof obj.id !== 'string') return false;
  if ('propertyName' in obj && typeof obj.propertyName !== 'string') return false;
  // eslint-disable-next-line no-use-before-define
  if ('filter' in obj && typeof obj.filter !== 'object' && !isFilterGroup(obj.filter)) return false;
  return true;
};

export const isFilterGroup = (obj: unknown): obj is FilterGroup => {
  if (!obj || typeof obj !== 'object') return false;

  if (!('isRootLevelFilter' in obj) || typeof obj.isRootLevelFilter !== 'boolean') return false;

  if (
    !('logicalOperator' in obj) ||
    !LogicalOperators.includes(obj.logicalOperator as LogicalOperator)
  )
    return false;

  if (!('fields' in obj) || !Array.isArray(obj.fields) || !obj.fields.every(isFilterField))
    return false;

  return true;
};

const isOrderBy = (obj: unknown): obj is OrderBy => {
  if (!obj || typeof obj !== 'object') return false;
  if (!('field' in obj) || typeof obj.field !== 'string') return false;
  if (!('sortOrder' in obj) || !SortOrders.includes(obj.sortOrder as SortOrder)) return false;
  return true;
};

const isOrderProvidersBy = (obj: unknown): obj is TOrderProvidersBy => {
  if (!obj || typeof obj !== 'object') return false;
  if (!('type' in obj) || !SortOrders.includes(obj.type as SortOrder)) return false;
  return true;
};

export const isSearchQuery = (obj: unknown): obj is SearchQuery => {
  if (!obj || typeof obj !== 'object') return false;

  if ('distinctBy' in obj && typeof obj.distinctBy !== 'string') return false;
  if ('queryText' in obj && typeof obj.queryText !== 'string') return false;

  if (!('indexName' in obj) || !Object.values(IndexName).includes(obj.indexName as IndexName))
    return false;

  if (!('filter' in obj) || !isFilterGroup(obj.filter)) return false;
  if ('orderBy' in obj && (!Array.isArray(obj.orderBy) || !obj.orderBy.every(isOrderBy)))
    return false;
  if ('orderProvidersBy' in obj && !isOrderProvidersBy(obj.orderProvidersBy)) return false;

  return true;
};
